import { useBuild, useRetriesForJob } from "~/components/build/Show/lib/BuildContext";
import { Link, useNavigate } from "react-router-dom";
import BuildPageStore from "~/stores/BuildPageStore";
import Job from "~/components/job/Job";
import { useCallback, useEffect } from "react";
import { CommandJob } from "~/components/build/Show/lib/types";
import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";
import Icon from "~/components/shared/Icon";
import ToolbarButton from "~/components/build/Show/components/ToolbarButton";
import { fetchBuildJob } from "~/components/build/Show/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import { LogLineSelectionProvider } from "~/components/build/Show/lib/useLogLineSelection";

type Props = {
  job: CommandJob | null | undefined;
  // It is being used!
  // eslint-disable-next-line react/no-unused-prop-types
  jobWithRetries?: CommandJob[];
};

export const RetryJobNavigation = (props: Props) => {
  const { build } = useBuild();
  const queryClient = useQueryClient();

  if (!props.job) {
    return null;
  }

  const jobWithRetries = props.jobWithRetries || useRetriesForJob(props.job);
  const index = jobWithRetries.findIndex((job) => job.id === props.job?.id);

  const prev = jobWithRetries[index - 1];
  const next = jobWithRetries[index + 1];

  if (jobWithRetries.length <= 1) {
    return null;
  }

  return (
    <div className="ml-auto flex items-center gap-2">
      <span className="text-xs">
        Retry <span className="tabular-nums">{index + 1}</span>
        {" of "}
        <span className="tabular-nums">{jobWithRetries.length}</span>
      </span>

      <div className="flex gap-1">
        <Link
          data-testid="previous-retry-link"
          to={prev && resolveJobUrl(prev.id)}
          onMouseEnter={() => {
            Features.ProgressiveBuildShow &&
              queryClient.prefetchQuery({
                queryKey: ["build", build.number, "jobs", prev.id],
                queryFn: () =>
                  fetchBuildJob({
                    buildDataBasePath: build.buildDataBasePath,
                    jobUuid: prev.id,
                    buildNumber: build.number,
                    queryClient,
                  }),
              });
          }}
          title="Previous"
          aria-disabled={!prev}
          className="text-inherit aria-disabled:pointer-events-none aria-disabled:text-gray-700"
        >
          <ToolbarButton>
            <Icon icon="heroicons/outline/chevron-left" className="h-4" />
          </ToolbarButton>
        </Link>
        <Link
          data-testid="next-retry-link"
          to={next && resolveJobUrl(next.id)}
          onMouseEnter={() => {
            Features.ProgressiveBuildShow &&
              queryClient.prefetchQuery({
                queryKey: ["build", build.number, "jobs", next.id],
                queryFn: () =>
                  fetchBuildJob({
                    buildDataBasePath: build.buildDataBasePath,
                    jobUuid: next.id,
                    buildNumber: build.number,
                    queryClient,
                  }),
              });
          }}
          title="Next"
          aria-disabled={!next}
          className="text-inherit aria-disabled:pointer-events-none aria-disabled:text-gray-700"
        >
          <ToolbarButton>
            <Icon icon="heroicons/outline/chevron-right" className="h-4" />
          </ToolbarButton>
        </Link>
      </div>
    </div>
  );
};

export const CommandJobDetails = ({ job }: Props) => {
  const { build, store } = useBuild();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (job) {
      BuildPageStore.expandJob(job);
    }
  }, [job?.id]);

  const handleRedirectToLatestJob = useCallback(
    (latestJobUUID: string) => {
      if (latestJobUUID) {
        Features.ProgressiveBuildShow &&
          queryClient.prefetchQuery({
            queryKey: ["build", build.number, "jobs", latestJobUUID],
            queryFn: () =>
              fetchBuildJob({
                buildDataBasePath: build.buildDataBasePath,
                jobUuid: latestJobUUID,
                buildNumber: build.number,
                queryClient,
              }),
          });

        navigate(resolveJobUrl(latestJobUUID));
      }
    },
    [navigate],
  );

  if (!job) {
    return null;
  }

  return (
    <LogLineSelectionProvider>
      <div className="job-list-pipeline flex h-full flex-col">
        <Job
          key={job.id}
          job={job}
          build={build}
          buildStore={store}
          autoFollow={true}
          onRetrySuccess={handleRedirectToLatestJob}
        />
      </div>
    </LogLineSelectionProvider>
  );
};
